import type { FloorRoom, Order } from "../../api/types";
import { FloorPlan } from "../../features/tables/FloorPlan";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { AppIcon } from "../AppIcon";
import { tablesByRoom } from "./waiter";

interface Props {
  floor: readonly FloorRoom[];
  openTables: ReadonlyMap<string, Order>;
  /** Tables with lines not sent to the kitchen yet (prototype: dashed outline, "niet verstuurd"). */
  withDraft: ReadonlySet<string>;
  /** The table open on the right (iPad). */
  selected: string | null;
  now: number;
  onTable: (table: string) => void;
}

/**
 * The tables as they stand in the shop (Furkan, 08/10/2026: "bij tafels aub grondplan tonen"): per room
 * the floor plan of Beheer › Tafelplan, the same as on the till; orange = occupied, dashed = something not
 * sent yet. On a phone the plan is small, so the occupied tables follow once more in a list, the one
 * waiting longest first, with the minutes at the table and what is still open.
 */
export function TablesView({ floor, openTables, withDraft, selected, now, onTable }: Props) {
  const text = t.tafoApp.waiter;
  const rooms = tablesByRoom(floor);
  const busy = rooms
    .flatMap(({ tables }) => tables)
    .flatMap((table) => {
      const order = openTables.get(table);
      return order === undefined ? [] : [{ table, order }];
    })
    .sort((a, b) => Date.parse(a.order.createdAt) - Date.parse(b.order.createdAt));
  return (
    <>
      {rooms.map(({ room }) => (
        <section key={room.id} aria-label={room.name} className="ob-room">
          <div className="ph-sec">{room.name}</div>
          <div className="fp-box">
            <FloorPlan
              room={room}
              openTables={openTables}
              current={selected ?? ""}
              now={now}
              enabled={() => true}
              onTable={onTable}
              withDraft={withDraft}
            />
          </div>
        </section>
      ))}
      {busy.length > 0 && (
        <section aria-label={text.busyList} className="ob-busy">
          <div className="ph-sec">{text.busyList}</div>
          {busy.map(({ table, order }) => {
            const minutes = Math.max(0, Math.round((now - Date.parse(order.createdAt)) / 60_000));
            return (
              <button
                key={table}
                type="button"
                className={`ob-busyrow${selected === table ? " sel" : ""}`}
                onClick={() => {
                  onTable(table);
                }}
              >
                <b>{text.table(table)}</b>
                <span>{text.busy(minutes, formatEuro(order.totalCents - order.paidCents))}</span>
                <AppIcon name="chevron" size={18} />
              </button>
            );
          })}
        </section>
      )}
      <p className="proto">{text.tablesNote}</p>
    </>
  );
}
